import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Truck, Wrench, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const BRANDS = ["Apple", "Samsung", "OnePlus", "Xiaomi", "Realme", "Oppo", "Vivo", "IQOO"];

export default function Home() {
  const { settings } = useSiteSettings();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data));
  }, []);

  const stats = [
    { n: settings.hero_stat_1_value, l: settings.hero_stat_1_label },
    { n: settings.hero_stat_2_value, l: settings.hero_stat_2_label },
    { n: settings.hero_stat_3_value, l: settings.hero_stat_3_label },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-16 lg:py-24 grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-white/10 bg-white/5 mono-label text-white/70 mb-6">
              <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full"/> {settings.trust_badge}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter mb-5">
              {settings.hero_title_prefix} <span className="text-blue-500">{settings.hero_title_highlight}</span>{settings.hero_title_suffix}
            </h1>
            <p className="text-base text-white/70 max-w-xl leading-relaxed">
              {settings.hero_subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid="hero-shop-btn" size="lg" className="bg-blue-600 hover:bg-blue-500 text-white rounded-none px-6">
                <Link to="/shop">Browse displays <ArrowRight className="ml-2 h-4 w-4"/></Link>
              </Button>
              <Button asChild data-testid="hero-call-btn" size="lg" variant="outline" className="border-white/20 bg-transparent hover:bg-white/5 rounded-none px-6">
                <a href={`tel:${(settings.business_phone || "").replace(/\s/g, "")}`}><PhoneCall className="mr-2 h-4 w-4"/> {settings.business_phone}</a>
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-3 border border-white/10 divide-x divide-white/10 max-w-lg">
              {stats.map((s) => (
                <div key={s.l} className="p-4">
                  <div className="font-display text-2xl font-medium">{s.n}</div>
                  <div className="mono-label text-white/50 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="grid grid-cols-6 gap-px bg-white/10">
              <div className="col-span-6 aspect-[16/10] bg-zinc-950 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/6754839/pexels-photo-6754839.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Technician working on smartphone"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="col-span-3 aspect-square bg-zinc-950 p-5 flex flex-col justify-between">
                <span className="mono-label text-blue-400">01 / Quality</span>
                <div>
                  <p className="font-display text-xl">OEM-grade panels</p>
                  <p className="text-xs text-white/60 mt-1">Bench-tested. Zero dead pixels.</p>
                </div>
              </div>
              <div className="col-span-3 aspect-square bg-zinc-950 p-5 flex flex-col justify-between">
                <span className="mono-label text-yellow-400">02 / Coverage</span>
                <div>
                  <p className="font-display text-xl">iPhone, Samsung, OnePlus, Xiaomi</p>
                  <p className="text-xs text-white/60 mt-1">50+ compatible models in stock.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand chips */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
          <p className="mono-label text-white/50 mb-4">Shop by brand</p>
          <div className="flex flex-wrap gap-2">
            {BRANDS.map((b) => (
              <Link
                key={b}
                to={`/shop?brand=${encodeURIComponent(b)}`}
                data-testid={`brand-chip-${b}`}
                className="px-5 py-2.5 border border-white/10 bg-zinc-950 hover:border-blue-500/60 hover:text-blue-400 text-sm transition-colors"
              >
                {b}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="mono-label text-blue-400 mb-2">Featured stock</p>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Popular displays right now</h2>
            </div>
            <Link to="/shop" data-testid="featured-see-all" className="text-sm text-white/70 hover:text-white flex items-center gap-1">
              See all <ArrowRight className="h-4 w-4"/>
            </Link>
          </div>
          <motion.div
            initial="hidden" animate="show"
            variants={{ show: { transition: { staggerChildren: 0.06 } } }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10"
          >
            {featured.map((p) => (
              <motion.div key={p.id} variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }} className="bg-zinc-950">
                <ProductCard p={p} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why us */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 grid gap-px sm:grid-cols-3 bg-white/10 border border-white/10">
          {[
            { icon: ShieldCheck, title: "Bench-tested", text: "Every panel is powered on and checked for dead pixels, touch response, and colour uniformity." },
            { icon: Truck, title: "Free shipping", text: "Free delivery to any pincode in India. Dispatched within 48 hours from Hyderabad." },
            { icon: Wrench, title: "Repair shop insight", text: "Curated by real technicians from Phone Experts store — no dropshipped mystery grades." },
          ].map((f) => (
            <div key={f.title} className="bg-zinc-950 p-8">
              <f.icon className="h-6 w-6 text-blue-500 mb-4"/>
              <h3 className="font-display text-xl mb-2">{f.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
